import type {
  AccessEvent,
  AccessRow,
  AdminUser,
  AgentTurn,
  ChangeEvent,
  Command,
  HistoryEntry,
  NodeDisplay,
  ProjectMember,
  ProposalDetail,
  ProposalSummary,
  ProjectRole,
  ProjectPrefs,
  ProjectPrefsPatch,
  ProjectState,
  ProjectSummary,
  PptxGeom,
  RestorePoint,
  RevertResult,
  ReviewResponse,
  ScenarioCompareResponse,
  SteercoPackRequestOptions,
  User,
  VarianceResponse,
} from '@integrate/contracts';
import type { SeparationState } from '@integrate/core';

/** What GET /design returns: the whole design, plus the display config that renders it. */
export interface DesignPayload {
  projectId: string;
  projectName: string;
  scenarioId: string;
  scenarioName: string;
  scenarios: { id: string; name: string; isDefault: boolean }[];
  role: ProjectRole;
  seq: number;
  state: SeparationState;
  nodeDisplay: NodeDisplay;
  pptxGeom: PptxGeom;
}

/**
 * What staging a roster refresh comes back with — the same shape `ProjectRepository.stageDataUpdate`
 * returns, declared here because the client cannot import from the server.
 *
 * ADVISORY BY CONSTRUCTION, and the field names say so. The apply recomputes the diff from the same
 * function against the roster as it stands then, so these counts describe the project at the moment of
 * the preview. `rosterCount` is what makes that safe rather than merely honest: it goes back with the
 * apply, which refuses if the roster moved underneath the numbers somebody approved.
 */
export interface StagedRefresh {
  runId: string;
  filename: string;
  rowCount: number;
  /** Columns the project maps that the file does not have. Non-empty means the apply WILL refuse. */
  missingColumns: string[];
  preview: {
    joiners: number;
    leavers: number;
    movers: number;
    unchanged: number;
    stillDeparted: number;
    /** Capped samples, so a reviewer sees people rather than counts. */
    sampleJoiners: { id: string; name: string }[];
    sampleLeavers: { id: string; name: string }[];
    sampleMovers: { id: string; name: string; fields: string[] }[];
  };
  rosterCount: number;
}

/**
 * What an import turned out to be.
 *
 * TWO SHAPES BEHIND ONE ROUTE, discriminated by `source`, because the operator's action is the same
 * — "import my file" — and only the file knows which kind it is. A project export reports what it
 * contained; a plain HR extract reports the mapping it was READ UNDER, because that mapping was
 * guessed and is the thing the user has to check.
 */
export type ImportOutcome =
  | {
      source: 'project';
      projectId: string;
      scenarioId: string;
      employeeCount: number;
      schemaVersion: string;
      dropped: string[];
    }
  | {
      source: 'roster';
      projectId: string;
      scenarioId: string;
      employeeCount: number;
      columnMap: Record<string, string>;
      groupingLevels: { key: string; label: string }[];
      /** Fields matched on a weak signal, or on a column name that says nothing. Worth checking. */
      lowConfidence: string[];
      /** Fields no column answered to at all. */
      unmapped: string[];
    };

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * The pack was ISSUED — event committed, restore point stamped — but the bytes never made it down.
 *
 * Its own class because the two failures demand opposite advice: a refused pack should be retried,
 * a delivered-but-dropped one must NOT be (a retry mints a second, real pack). The seq is carried
 * so the dialog can name the change that already exists.
 */
export class PackDeliveryError extends Error {
  constructor(readonly seq: number) {
    super('The pack was issued, but the download could not be completed.');
    this.name = 'PackDeliveryError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    // The session is an httpOnly cookie, so it must ride along on every call.
    credentials: 'same-origin',
    headers: {
      ...(init.body && !(init.body instanceof FormData) ? { 'content-type': 'application/json' } : {}),
      ...init.headers,
    },
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const body = text ? (JSON.parse(text) as unknown) : undefined;

  if (!response.ok) {
    const message =
      body && typeof body === 'object' && 'error' in body
        ? String((body as { error: unknown }).error)
        : `Request failed (${response.status})`;
    throw new ApiError(response.status, message);
  }
  return body as T;
}

export const api = {
  login: (email: string, password: string) =>
    request<{ user: User }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),

  me: () =>
    request<{
      user: User;
      /** Three Azure deployments, three independent absences — see `/api/me` on the server. */
      assistant: { available: boolean; voice: boolean; realtime: boolean };
    }>('/api/me'),

  /**
   * Change your own name — the only thing on your account besides the password that you may set.
   *
   * The name is what every other person sees: the presence strip, every change-log row, every
   * proposal. It could be set once at account creation and never again, by anybody, including an
   * administrator.
   */
  updateOwnProfile: (name: string) =>
    request<{ user: User }>('/api/me', {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    }),

  /*
    User administration.

    NOT PROJECT-SCOPED, unlike everything below it, which is why these sit up here beside `me` rather
    than among the project calls. An account exists before any project does — `MembersEditor` grants
    access by email and can only reach somebody an administrator has already created — so this is the
    surface that has to come first, and until now it existed only as `curl`.
  */
  adminUsers: () => request<{ users: AdminUser[] }>('/api/admin/users'),

  /** Every grant on the deployment: who reaches which project, and as what. Administrators only. */
  adminAccess: () => request<{ access: AccessRow[] }>('/api/admin/access'),

  /**
   * What has HAPPENED to people's access — created, deactivated, promoted, granted, removed.
   *
   * The counterpart to `adminAccess`, which is the state. Neither answers the other's question: a
   * grant list cannot tell you that somebody was removed, and a log cannot tell you at a glance who
   * is on what today.
   */
  adminAccessLog: (filter: { subjectUserId?: string; projectId?: string } = {}) => {
    const query = new URLSearchParams();
    if (filter.subjectUserId) query.set('subjectUserId', filter.subjectUserId);
    if (filter.projectId) query.set('projectId', filter.projectId);
    const suffix = query.toString();
    return request<{ events: AccessEvent[] }>(
      `/api/admin/access-log${suffix ? `?${suffix}` : ''}`,
    );
  },

  createUser: (input: { email: string; name: string; password: string; isAdmin: boolean }) =>
    request<{ user: User }>('/api/admin/users', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /** Rename, promote, demote, deactivate or reactivate somebody. Never yourself — the server refuses. */
  updateUser: (userId: string, patch: { name?: string; isAdmin?: boolean; isActive?: boolean }) =>
    request<{ user: AdminUser }>(`/api/admin/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  /** For somebody who cannot sign in to do it themselves. Ends their sessions. */
  setUserPassword: (userId: string, password: string) =>
    request<void>(`/api/admin/users/${userId}/password`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    }),

  /**
   * Change your own, proving you know the current one.
   *
   * Ends your OTHER sessions and reissues this one, so the tab making the call stays signed in —
   * the cookie is replaced by the response and nothing here has to know that happened.
   */
  changePassword: (currentPassword: string, newPassword: string) =>
    request<void>('/api/me/password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),

  projects: (options: { includeArchived?: boolean } = {}) =>
    request<{ projects: ProjectSummary[] }>(
      `/api/projects${options.includeArchived ? '?includeArchived=true' : ''}`,
    ),

  /**
   * Destroy a project and everything in it, including its change log.
   *
   * The name is typed back rather than a dialog being clicked through, and that requirement is the
   * SERVER'S (`app.ts`'s delete route) — this passes it on rather than inventing it, so the control
   * and the check agree about what confirmation means. Distinct from archiving, which is a command
   * and is reversible.
   */
  deleteProject: (projectId: string, confirmName: string) =>
    request<void>(`/api/projects/${projectId}`, {
      method: 'DELETE',
      body: JSON.stringify({ confirmName }),
    }),

  importProject: (file: File, options: { name?: string; keepRaw?: boolean } = {}) => {
    const form = new FormData();
    if (options.name) form.append('name', options.name);
    form.append('keepRaw', String(options.keepRaw ?? false));
    // The file part must come last: the server reads the scalar fields off the file part, which
    // busboy only populates from parts seen before it.
    form.append('file', file);
    return request<ImportOutcome>('/api/projects/import', { method: 'POST', body: form });
  },

  /**
   * Stage a roster refresh and get back what it would do. Writes nothing to the design.
   *
   * Two steps rather than one because `applyDataUpdate` is the most consequential command in the
   * catalogue: it replaces the population, marks leavers, re-applies the perimeter rule and reconciles
   * every scenario. A client that posted the file and reported a count would be reproducing the shipped
   * app's worst property — the refresh whose effects nobody saw coming.
   */
  stageDataUpdate: (projectId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<StagedRefresh>(`/api/projects/${projectId}/data-update`, {
      method: 'POST',
      body: form,
    });
  },

  projectState: (projectId: string) => request<ProjectState>(`/api/projects/${projectId}/state`),

  /**
   * The full design, which is what everything on screen is a function of.
   *
   * Distinct from projectState, which is the walking skeleton's narrow shape — a roster plus one bucket
   * per person. Perspectives, group rollups, heads, overrides and the stage lens all need the whole
   * SeparationState, and core is shared so the client runs the same engines against it as the server.
   */
  projectDesign: (projectId: string, scenarioId?: string) =>
    request<DesignPayload>(
      `/api/projects/${projectId}/design${scenarioId ? `?scenarioId=${encodeURIComponent(scenarioId)}` : ''}`,
    ),

  /**
   * This tab's saved filters. Not a command, so not part of the design payload either.
   *
   * A separate round trip on open is the price of keeping per-user state out of shared state; it is
   * one small request, and it is what stops a filter appearing on somebody else's screen.
   */
  /**
   * Every scenario priced over the same roster — the compare pane's rows.
   *
   * Computed server-side because the store holds ONE design at a time: pricing a sibling here would
   * mean fetching and holding every design, and the server already runs the same engines for the
   * deck's compare slide. No parameters — the roster and assumptions are project-level, and every
   * row is priced under its own design, so there is nothing for a caller to choose.
   */
  scenarioCompare: (projectId: string) =>
    request<ScenarioCompareResponse>(`/api/projects/${projectId}/scenario-compare`),

  /**
   * The latest scheduled review, and what has moved since the one before it.
   *
   * `run` is null when nobody has ever reviewed this project, which is every project until a
   * deployment sets `ANALYST_INTERVAL_MS` — and is a different answer from a review that found
   * nothing. A RESTRICTED member gets a 403 here, deliberately: a run counts the whole organisation
   * and the Diagnostics screen already reviews what they can see.
   */
  review: (projectId: string) => request<ReviewResponse>(`/api/projects/${projectId}/review`),

  /**
   * Every departure recorded against one scenario, beside the plan the refresh cleared.
   *
   * THE SCENARIO IS EXPLICIT, unlike `scenarioCompare`, which asks a project-level question. A plan
   * belongs to a design — "we had them down for a rightsizing at M12 and they resigned in March" is
   * a claim about one option, not about the deal — so the pane names the scenario on screen rather
   * than letting the route fall back to whichever one this user last opened. The two are the same
   * scenario in practice; saying it is what stops them diverging.
   *
   * NO SALARY GATE, unlike the money panes: the ledger is months, actions and names, so a member
   * whose grant withholds compensation reads it whole. A SCOPED member is served too — rows outside
   * their slice arrive with the name nulled, and still count.
   */
  variance: (projectId: string, scenarioId: string) =>
    request<VarianceResponse>(
      `/api/projects/${projectId}/variance?scenarioId=${encodeURIComponent(scenarioId)}`,
    ),

  /**
   * Issue a SteerCo pack: an ACT, not a read — it appends an event and stamps a restore point, so
   * unlike every other export it is a POST through fetch rather than an `<a href>` navigation. The
   * response is the zip itself; the dialog turns the blob into a download and shows the change
   * number from the header, which is the one fact worth saying out loud: what seq the pack froze.
   */
  steercoPack: async (
    projectId: string,
    body: SteercoPackRequestOptions,
  ): Promise<{ blob: Blob; filename: string; seq: number }> => {
    const response = await fetch(`/api/projects/${projectId}/steerco-pack`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const text = await response.text();
      let message = `Request failed (${response.status})`;
      try {
        const parsed = JSON.parse(text) as { error?: unknown };
        if (parsed && typeof parsed === 'object' && 'error' in parsed) message = String(parsed.error);
      } catch {
        /* a proxy error page is not JSON; the status line above is the honest fallback */
      }
      throw new ApiError(response.status, message);
    }
    /*
      PAST THIS LINE THE PACK EXISTS. An OK status means the transaction committed: the event is in
      the log and the restore point is stamped, whatever happens to the bytes on their way down. A
      failure below must therefore NOT look like "the pack failed" — a reader who believed that and
      clicked again would mint a second, real pack. `PackDeliveryError` carries the seq so the
      dialog can say exactly that.
    */
    const seq = Number(response.headers.get('x-steerco-seq') ?? '0');
    try {
      const blob = await response.blob();
      const filename =
        /filename="([^"]+)"/.exec(response.headers.get('content-disposition') ?? '')?.[1] ??
        'steerco-pack.zip';
      return { blob, filename, seq };
    } catch {
      throw new PackDeliveryError(seq);
    }
  },

  prefs: (projectId: string) => request<ProjectPrefs>(`/api/projects/${projectId}/prefs`),

  savePrefs: (projectId: string, patch: ProjectPrefsPatch) =>
    request<ProjectPrefs>(`/api/projects/${projectId}/prefs`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  events: (projectId: string, after: number) =>
    request<{ events: ChangeEvent[] }>(`/api/projects/${projectId}/events?after=${after}`),

  /**
   * The change log, newest first.
   *
   * Distinct from `events`, which is the realtime catch-up feed and answers "what have I missed since
   * seq N". This answers "what has happened to this project, and what can I take back" — so it carries
   * `revertable` and `undone`, which the socket frames do not, and it is fetched rather than streamed
   * because both flags change when somebody else acts.
   */
  history: (projectId: string, limit?: number) =>
    request<{ events: HistoryEntry[] }>(
      `/api/projects/${projectId}/history${limit === undefined ? '' : `?limit=${limit}`}`,
    ),

  /**
   * The points this project can be restored to, oldest first.
   *
   * Its own request rather than a field on the history: the two answer different questions, and the
   * table behind this one holds a serialised copy of the whole project per row. Fetched when the
   * history modal opens, which is the only place it is offered.
   */
  restorePoints: (projectId: string) =>
    request<{ points: RestorePoint[] }>(`/api/projects/${projectId}/restore-points`),

  /**
   * Who can see this project, and in what capacity.
   *
   * Readable by any member, not only an owner: the roster answers "who else is in here" as much as it
   * answers "who may I remove", and a reviewer who can see a colleague's name in the change log and on
   * the presence strip can hardly be kept from a list of the same names.
   */
  members: (projectId: string) =>
    request<{ members: ProjectMember[] }>(`/api/projects/${projectId}/members`),

  /**
   * Grant access, or change the access somebody already has — one call, because the route upserts.
   *
   * By EMAIL rather than by user id, which is how an owner thinks about it and the only identifier they
   * have for a colleague who is not yet in the project. The server refuses an address it does not know
   * rather than creating an account, so an invitation to somebody who has never signed in is a 404 and
   * says so; that is a real limit and the panel states it.
   */
  /**
   * Grant access, change a role, or change what somebody may see. One upsert, three acts.
   *
   * `access` IS OPTIONAL AND OMISSION MEANS "LEAVE IT". A role change must not silently clear a scope
   * somebody was given, so `undefined` keeps and `null` removes — a distinction that usually does not
   * matter and does here.
   */
  addMember: (
    projectId: string,
    email: string,
    role: ProjectRole,
    access: { scope?: Record<string, unknown> | null; salaryHidden?: boolean } = {},
  ) =>
    request<void>(`/api/projects/${projectId}/members`, {
      method: 'POST',
      body: JSON.stringify({ email, role, ...access }),
    }),

  /**
   * Withdraw access, which also cuts that person's open tabs (`app.ts`, the delete route).
   *
   * Keyed by user id and not by email: the row being deleted is the membership, and an owner acting on a
   * list they are looking at has the id in hand. The last owner cannot be removed, including by
   * themselves, and the server refuses rather than the client hiding the button — a refusal the reader
   * can read beats a control that is silently absent.
   */
  removeMember: (projectId: string, userId: string) =>
    request<void>(`/api/projects/${projectId}/members/${userId}`, { method: 'DELETE' }),

  /**
   * Every proposal on the project, newest first.
   *
   * Readable by any member, like the change log and for the same reason: a proposal is a conversation
   * about the project, not a private draft. What the role gates is applying one.
   */
  proposals: (projectId: string) =>
    request<{ proposals: ProposalSummary[] }>(`/api/projects/${projectId}/proposals`),

  /** One proposal, with the commands themselves — which the list deliberately does not carry. */
  proposal: (projectId: string, proposalId: string) =>
    request<{ proposal: ProposalDetail }>(`/api/projects/${projectId}/proposals/${proposalId}`),

  /**
   * Ask the assistant for a change.
   *
   * Returns the whole turn rather than just the proposal, because two of the three outcomes are not
   * proposals: the model may answer in words (`proposal: null`, `text` set), and it may have had calls
   * refused (`rejected`). A client that only read the proposal would show nothing at all for the first
   * and would silently drop the second, which is the failure `propose.ts` exists to prevent one layer
   * down.
   */
  askAssistant: (projectId: string, body: { prompt: string; scenarioId?: string | null }) =>
    request<AgentTurn>(`/api/projects/${projectId}/agent`, {
      method: 'POST',
      body: JSON.stringify({ scenarioId: null, ...body }),
    }),

  /**
   * The caller's own conversation in this project, oldest first.
   *
   * ONE TYPE FOR BOTH, and that is the point of `AgentTurn` living in contracts: what comes back from
   * an ask and what is in the transcript are the same thing, and two shapes would drift the moment one
   * of them gained a field.
   *
   * Not cached in the store. The thread is per user per project and is read when the dock opens, which
   * is also the moment somebody wants to see what another machine of theirs added.
   */
  assistantThread: (projectId: string) =>
    request<{ turns: AgentTurn[] }>(`/api/projects/${projectId}/agent/turns`),

  /**
   * Delete this conversation. Only the caller's, only in this project, and really deleted.
   *
   * This is the whole retention control — there is no expiry job — so it has to mean what it says.
   */
  clearAssistantThread: (projectId: string) =>
    request<void>(`/api/projects/${projectId}/agent/turns`, { method: 'DELETE' }),

  /**
   * A recording in, a sentence out. Nothing is proposed and nothing is stored.
   *
   * `FormData` rather than JSON, and the blob's own type is what the server matches against the shared
   * container list — so `recording.mimeType` travels as the part's content type rather than as a field
   * somebody has to remember to set. The FILENAME here is a courtesy for a server log and nothing more:
   * the server derives the one that reaches the service from the matched container and never reads this,
   * because a filename ends up in a multipart header.
   */
  transcribe: (projectId: string, recording: { blob: Blob; filename: string }) => {
    const form = new FormData();
    form.append('audio', recording.blob, recording.filename);
    return request<{ text: string }>(`/api/projects/${projectId}/transcribe`, {
      method: 'POST',
      body: form,
    });
  },

  /**
   * Suggest a change you may not make.
   *
   * `transcript` is what the proposer was reading, or an agent's reasoning — stored for the record and
   * never executed. It is already in the schema because P6's voice path ends here.
   */
  createProposal: (
    projectId: string,
    body: { title: string; commands: Command[]; scenarioId?: string | null; transcript?: string | null },
  ) =>
    request<{ proposal: ProposalSummary }>(`/api/projects/${projectId}/proposals`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * Apply it, as yourself.
   *
   * The commands run through the same path as if the applying editor had typed them — same validation,
   * same handlers, one transaction, one batch id — so the events say the applier made these changes,
   * because they did. Who suggested it is on the proposal.
   */
  applyProposal: (projectId: string, proposalId: string, note?: string) =>
    request<{ proposal: ProposalSummary; seqs: number[] }>(
      `/api/projects/${projectId}/proposals/${proposalId}/apply`,
      { method: 'POST', body: JSON.stringify({ note }) },
    ),

  /**
   * Close it without applying — two words, because they mean different things six months later.
   *
   * `rejected` is somebody with the authority deciding against it; `withdrawn` is the proposer taking it
   * back. The server decides which of the two a given reader may issue.
   */
  closeProposal: (
    projectId: string,
    proposalId: string,
    status: 'rejected' | 'withdrawn',
    note?: string,
  ) =>
    request<{ proposal: ProposalSummary }>(
      `/api/projects/${projectId}/proposals/${proposalId}/close`,
      { method: 'POST', body: JSON.stringify({ status, note }) },
    ),

  /**
   * Undo a change by running the inverse its handler recorded.
   *
   * Naming any event of a batch reverts the whole batch, because that is the unit that was applied.
   * The revert is a new change: it arrives back through the socket like any other, so there is nothing
   * to apply optimistically here — and deliberately so, since the server decides whether it is allowed
   * at all and a client that pre-applied it would have to unwind a refusal.
   */
  revert: (projectId: string, seq: number) =>
    request<RevertResult>(`/api/projects/${projectId}/history/${seq}/revert`, { method: 'POST' }),

  /** HTTP fallback for a command when the socket is not connected. */
  sendCommand: (projectId: string, command: Command) =>
    request<{ event: ChangeEvent }>(`/api/projects/${projectId}/commands`, {
      method: 'POST',
      body: JSON.stringify({ command }),
    }),

  /**
   * Several commands as one atomic change.
   *
   * The same endpoint, and the same transaction: the batch lands whole or not at all, under one
   * `batch_id`, so undo takes it off as one step. That is the point of it rather than a convenience —
   * adding a group of Day 1 transfers is a bucket write and a stage write per person, and half of that
   * applied is a state nobody asked for.
   */
  sendCommands: (projectId: string, commands: readonly Command[]) =>
    request<{ events: ChangeEvent[] }>(`/api/projects/${projectId}/commands`, {
      method: 'POST',
      body: JSON.stringify({ commands }),
    }),
};
